import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';

import { planFor, useBulkAnalysis } from '../api/bulkAnalysis';
import { MAX_BULK_DATASETS } from '../model/bulkAnalysis';
import type { AnalysisKind, BulkPlan } from '../model/bulkAnalysis';

/* **Run Profile** and **Classify PII**, each across every dataset of one source.
 *
 * **Two controls rather than one, because they are two decisions.** A single "run profile, classify
 * PII" button made the cheap action inseparable from the expensive one: a classification reads
 * `information_schema` and a model, where a profile is roughly 20 full `GROUP BY` scans of a
 * customer's production table. A reader who wanted PII coverage across a source had no way to ask
 * for it without also spending a few thousand table scans.
 *
 * **Confirmed, and the confirmation carries the plan rather than a generic question.** The plan is
 * fetched *first*, so the dialog states how many datasets this will actually touch and how many are
 * skipped because the work is already done. A dialog reading "run for all datasets?" would ask a
 * reader to approve a cost neither of them had counted. That ordering also makes the answer honest
 * when there is nothing to do: a source already fully profiled says so instead of starting a run
 * that settles instantly with a spinner in between.
 *
 * **The buttons report themselves independently.** Each asks whether *its own* kind is the one in
 * flight, so a running profile leaves Classify PII pressable — the runner is what serialises the
 * two, and it says which one is holding the other up. A shared `busy` flag would disable both,
 * which for two operations that do not contend would read as the screen being stuck.
 *
 * **The individual actions on `DatasetDetail` are untouched.** Both paths go through the same two
 * request functions, so what changes for a dataset a bulk run covered is only that the work has
 * already happened.
 */

export interface BulkAnalysisControlProps {
  readonly connectionId: string;
  /** The search the table is filtered by, so a run covers what the reader can see.
   *
   * A reader who has typed a filter and pressed one of these means "these datasets". Ignoring it
   * would run against the whole source, which is a wider action than the screen appears to offer.
   */
  readonly search: string;
  /** True when the listing came back empty, which is what disables both actions.
   *
   * Passed in rather than re-read: the screen has already asked, and a second read here could
   * disagree with the table the reader is looking at. Requirement 17's empty case — the actions are
   * disabled rather than pressable-and-then-explaining, so no request is made at all.
   */
  readonly empty: boolean;
}

const ORDER: readonly AnalysisKind[] = ['profile', 'classify'];

export function BulkAnalysisControl({
  connectionId,
  search,
  empty,
}: BulkAnalysisControlProps): ReactElement {
  const { t } = useTranslation();
  const bulk = useBulkAnalysis();
  /* Which action is being planned, or `null`. One slot rather than two booleans: only one dialog
     can be open, so two flags would admit a state where both are set. */
  const [planning, setPlanning] = useState<AnalysisKind | null>(null);
  const [plan, setPlan] = useState<BulkPlan | null>(null);
  /* A planning failure only. A *run's* failures are counted in its own progress line, because a
     bulk run's error report is its breakdown. Cleared on each fresh press so a refusal cannot
     outlive the attempt it described. */
  const [planError, setPlanError] = useState<string | null>(null);

  async function openPlan(kind: AnalysisKind): Promise<void> {
    setPlanning(kind);
    setPlanError(null);
    try {
      setPlan(await planFor(connectionId, search, kind));
    } catch (failure) {
      setPlanError(
        failure instanceof Error ? failure.message : t('catalogue.bulk.planFailed'),
      );
    } finally {
      setPlanning(null);
    }
  }

  return (
    <>
      {/* The wrapper is what aligns these with the search field beside them and keeps them
          together when the row wraps — see `.lq-toolbar__actions`. */}
      <div className="lq-toolbar__actions">
        {ORDER.map((kind) => {
          const isRunning = bulk.running && bulk.kind === kind;
          return (
            <Button
              key={kind}
              /* `secondary` for both. The primary tone is this kit's "the action this screen
                 exists for", and a listing's subject is its rows — two filled buttons above a
                 table would be the loudest thing on a page a reader came here to read. */
              tone="secondary"
              busy={planning === kind || isRunning}
              /* Refused while *this* action is being planned or run, and while the other one is
                 running — the runner serialises the two. Refused on an empty listing, so no
                 request is made for a source with nothing in it. */
              disabled={empty || planning !== null || bulk.running}
              title={t(`catalogue.bulk.${kind}.tooltip`)}
              onClick={() => {
                void openPlan(kind);
              }}
            >
              {isRunning
                ? t(`catalogue.bulk.${kind}.busy`)
                : t(`catalogue.bulk.${kind}.action`)}
            </Button>
          );
        })}
      </div>

      <ConfirmDialog
        open={plan !== null}
        /* `primary`, not the default `danger`: this writes nothing a reader would have to undo and
           destroys nothing. It is expensive rather than dangerous, and the delete tone would put
           "measure my data" in the same visual language as "retire my connection". */
        tone="primary"
        title={plan === null ? '' : t(`catalogue.bulk.${plan.kind}.confirmTitle`)}
        message={
          plan === null ? null : (
            <>
              <p>
                {t(`catalogue.bulk.${plan.kind}.confirmBody`, {
                  datasets: plan.considered,
                  count: plan.tasks.length,
                })}
              </p>
              {/* Named, because a skip is the reason the number above is smaller than the dataset
                  count — without it a reader reads "12 of 32" as the control having missed
                  twenty. */}
              {plan.skipped > 0 ? (
                <p className="lq-muted">
                  {t(`catalogue.bulk.${plan.kind}.confirmSkipped`, {
                    count: plan.skipped,
                  })}
                </p>
              ) : null}
              {/* **"We could not tell" is not "nothing to do".** Without this a failed facts read
                  would show an empty plan, which reads as "already done" — the inverse of the
                  truth, on the one action whose mistake costs table scans. */}
              {plan.unknownStatus ? (
                <p className="lq-muted">{t('catalogue.bulk.unknownStatus')}</p>
              ) : null}
              {plan.considered >= MAX_BULK_DATASETS ? (
                <p className="lq-muted">
                  {t('catalogue.bulk.confirmCapped', { cap: MAX_BULK_DATASETS })}
                </p>
              ) : null}
              <p className="lq-muted">{t(`catalogue.bulk.${plan.kind}.cost`)}</p>
            </>
          )
        }
        confirmLabel={
          plan === null || plan.tasks.length === 0
            ? t('catalogue.bulk.confirmNothing')
            : t(`catalogue.bulk.${plan.kind}.confirmRun`)
        }
        cancelLabel={t('common.cancel')}
        onConfirm={() => {
          if (plan !== null) bulk.start(plan);
          setPlan(null);
        }}
        onCancel={() => {
          setPlan(null);
        }}
      />

      {planError !== null ? (
        <Alert tone="error" title={t('catalogue.bulk.planFailed')}>
          {planError}
        </Alert>
      ) : null}

      {/* **The breakdown rather than a bar.** What a reader needs from a run of N independent
          requests is how many are left and whether any failed — a percentage hides the failures
          inside the same number as the successes. `role="status"` so progress and completion are
          announced rather than only rendered, which for an operation that can take minutes is the
          difference between watching the screen and being told.

          A finished run that lost datasets is an `Alert`, not a muted line: a partial failure is
          the one outcome here somebody has to act on, and the platform's warning tone is how every
          other screen says so. A clean run stays quiet. */}
      {bulk.started && bulk.kind !== null && (bulk.running || bulk.failed === 0) ? (
        <p role="status" className="lq-muted">
          {bulk.running
            ? t(`catalogue.bulk.${bulk.kind}.progress`, {
                done: bulk.done,
                total: bulk.total,
              })
            : t(`catalogue.bulk.${bulk.kind}.done`, { total: bulk.total })}
        </p>
      ) : null}
      {bulk.started && bulk.kind !== null && !bulk.running && bulk.failed > 0 ? (
        <Alert tone="warning" title={t(`catalogue.bulk.${bulk.kind}.partialTitle`)}>
          <span role="status">
            {t(`catalogue.bulk.${bulk.kind}.partialBody`, {
              done: bulk.done,
              failed: bulk.failed,
              total: bulk.total,
            })}
          </span>
        </Alert>
      ) : null}
    </>
  );
}
